/**
 * 収集地区設定サービス（統合版）
 * 本番API、モックAPI、環境切り替えを一つのファイルで管理
 */

import { USE_MOCK_API, API_BASE_URL, IS_DEVELOPMENT } from '../config.js'

// =============================================================================
// モックデータとAPI
// =============================================================================

// モックデータ
let mockDistricts = [
  {
    id: 1,
    districtName: '中央地区',
    createdAt: '2024-01-15T09:00:00.000Z',
    updatedAt: '2024-01-15T09:00:00.000Z'
  },
  {
    id: 2,
    districtName: '東部地区',
    createdAt: '2024-01-20T10:30:00.000Z',
    updatedAt: '2024-01-20T10:30:00.000Z'
  },
  {
    id: 3,
    districtName: '西部地区',
    createdAt: '2024-02-01T14:15:00.000Z',
    updatedAt: '2024-02-01T14:15:00.000Z'
  },
  {
    id: 4,
    districtName: '南部地区',
    createdAt: '2024-02-10T16:45:00.000Z',
    updatedAt: '2024-02-10T16:45:00.000Z'
  },
  {
    id: 5,
    districtName: '北部地区',
    createdAt: '2024-03-01T11:20:00.000Z',
    updatedAt: '2024-03-01T11:20:00.000Z'
  }
]

// モック用ユーティリティ
const simulateDelay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms))
const shouldSimulateError = () => IS_DEVELOPMENT && Math.random() < 0.1

// =============================================================================
// モックAPI実装
// =============================================================================

const mockApi = {
  async getCollectionDistricts() {
    await simulateDelay()

    if (shouldSimulateError()) {
      throw new Error('ネットワークエラーが発生しました（モック）')
    }

    return {
      success: true,
      data: [...mockDistricts],
      total: mockDistricts.length,
      timestamp: new Date().toISOString()
    }
  },

  async createCollectionDistrict(districtData) {
    await simulateDelay()

    // 重複チェック（地区名）
    const isDuplicateName = mockDistricts.some(district =>
      district.districtName === districtData.districtName
    )

    if (isDuplicateName) {
      throw new Error('この地区名は既に登録されています')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 新しい地区を作成
    const newDistrict = {
      id: Math.max(...mockDistricts.map(district => district.id)) + 1,
      districtName: districtData.districtName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    mockDistricts.push(newDistrict)

    return {
      success: true,
      data: newDistrict,
      message: '収集地区が正常に作成されました'
    }
  },

  async updateCollectionDistrict(id, districtData) {
    await simulateDelay()

    const districtId = parseInt(id)
    const districtIndex = mockDistricts.findIndex(district => district.id === districtId)

    if (districtIndex === -1) {
      throw new Error('指定された地区が見つかりません')
    }

    // 重複チェック（編集対象以外との地区名重複）
    const isDuplicateName = mockDistricts.some(district =>
      district.id !== districtId && district.districtName === districtData.districtName
    )

    if (isDuplicateName) {
      throw new Error('この地区名は既に登録されています')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 地区を更新
    const updatedDistrict = {
      ...mockDistricts[districtIndex],
      districtName: districtData.districtName,
      updatedAt: new Date().toISOString()
    }

    mockDistricts[districtIndex] = updatedDistrict

    return {
      success: true,
      data: updatedDistrict,
      message: '収集地区が正常に更新されました'
    }
  },

  async deleteCollectionDistrict(id) {
    await simulateDelay()

    const districtId = parseInt(id)
    const districtIndex = mockDistricts.findIndex(district => district.id === districtId)

    if (districtIndex === -1) {
      throw new Error('指定された地区が見つかりません')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 削除制約チェック（例：最後の地区は削除不可）
    if (mockDistricts.length <= 1) {
      throw new Error('最後の地区は削除できません')
    }

    const deletedDistrict = mockDistricts.splice(districtIndex, 1)[0]

    return {
      success: true,
      data: deletedDistrict,
      message: '収集地区が正常に削除されました'
    }
  }
}

// =============================================================================
// 本番API実装
// =============================================================================

const productionApi = {
  async getCollectionDistricts() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-districts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
          // 'Authorization': `Bearer ${getAuthToken()}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to fetch collection districts:', error)
      throw new Error('収集地区一覧の取得に失敗しました')
    }
  },

  async createCollectionDistrict(districtData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-districts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
        },
        body: JSON.stringify(districtData)
      })

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('この地区名は既に登録されています')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to create collection district:', error)
      if (error.message.includes('既に登録されています')) {
        throw error
      }
      throw new Error('収集地区の作成に失敗しました')
    }
  },

  async updateCollectionDistrict(id, districtData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-districts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
        },
        body: JSON.stringify(districtData)
      })

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('この地区名は既に登録されています')
        }
        if (response.status === 404) {
          throw new Error('指定された地区が見つかりません')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to update collection district:', error)
      if (error.message.includes('既に登録されています') || error.message.includes('見つかりません')) {
        throw error
      }
      throw new Error('収集地区の更新に失敗しました')
    }
  },

  async deleteCollectionDistrict(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-districts/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
        }
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('指定された地区が見つかりません')
        }
        if (response.status === 409) {
          throw new Error('この地区は使用中のため削除できません')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error('Failed to delete collection district:', error)
      if (error.message.includes('見つかりません') || error.message.includes('削除できません')) {
        throw error
      }
      throw new Error('収集地区の削除に失敗しました')
    }
  }
}

// =============================================================================
// 統合サービスインターフェース
// =============================================================================

// 環境に応じたAPIサービスを選択
const apiService = USE_MOCK_API ? mockApi : productionApi

if (USE_MOCK_API) {
  console.log('🔧 Using mock API for collection districts')
} else {
  console.log('🌐 Using production API for collection districts')
}

/**
 * 収集地区一覧を取得
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const getCollectionDistricts = async () => {
  try {
    const response = await apiService.getCollectionDistricts()

    // レスポンス形式を統一
    if (response.success !== undefined) {
      // モックAPIのレスポンス形式の場合
      return response
    } else {
      // 直接配列が返される場合
      return {
        success: true,
        data: response,
        total: Array.isArray(response) ? response.length : 0
      }
    }
  } catch (error) {
    console.error('Service: Failed to get collection districts:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集地区一覧の取得に失敗しました'
    }
  }
}

/**
 * 新しい収集地区を作成
 * @param {Object} districtData - 地区データ
 * @param {string} districtData.districtName - 地区名（必須）
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const createCollectionDistrict = async (districtData) => {
  try {
    // バリデーション
    if (!districtData?.districtName?.trim()) {
      return {
        success: false,
        data: [],
        message: '地区名は必須です'
      }
    }

    if (districtData.districtName.length > 20) {
      return {
        success: false,
        data: [],
        message: '地区名は20文字以内で入力してください'
      }
    }

    const cleanData = {
      districtName: districtData.districtName.trim()
    }

    const response = await apiService.createCollectionDistrict(cleanData)

    // 作成後の全データを取得
    const allDistricts = await apiService.getCollectionDistricts()

    return {
      success: true,
      data: allDistricts.data || allDistricts,
      message: response.message || '収集地区が正常に作成されました'
    }
  } catch (error) {
    console.error('Service: Failed to create collection district:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集地区の作成に失敗しました'
    }
  }
}

/**
 * 収集地区を更新
 * @param {string|number} id - 地区ID
 * @param {Object} districtData - 更新する地区データ
 * @param {string} districtData.districtName - 地区名（必須）
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const updateCollectionDistrict = async (id, districtData) => {
  try {
    // バリデーション
    if (!id) {
      return {
        success: false,
        data: [],
        message: '地区IDが指定されていません'
      }
    }

    if (!districtData?.districtName?.trim()) {
      return {
        success: false,
        data: [],
        message: '地区名は必須です'
      }
    }

    if (districtData.districtName.length > 20) {
      return {
        success: false,
        data: [],
        message: '地区名は20文字以内で入力してください'
      }
    }

    const cleanData = {
      districtName: districtData.districtName.trim()
    }

    const response = await apiService.updateCollectionDistrict(id, cleanData)

    // 更新後の全データを取得
    const allDistricts = await apiService.getCollectionDistricts()

    return {
      success: true,
      data: allDistricts.data || allDistricts,
      message: response.message || '収集地区が正常に更新されました'
    }
  } catch (error) {
    console.error('Service: Failed to update collection district:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集地区の更新に失敗しました'
    }
  }
}

/**
 * 収集地区を削除
 * @param {string|number} id - 地区ID
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const deleteCollectionDistrict = async (id) => {
  try {
    // バリデーション
    if (!id) {
      return {
        success: false,
        data: [],
        message: '地区IDが指定されていません'
      }
    }

    const response = await apiService.deleteCollectionDistrict(id)

    // 削除後の全データを取得
    const allDistricts = await apiService.getCollectionDistricts()

    return {
      success: true,
      data: allDistricts.data || allDistricts,
      message: response.message || '収集地区が正常に削除されました'
    }
  } catch (error) {
    console.error('Service: Failed to delete collection district:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集地区の削除に失敗しました'
    }
  }
}

/**
 * 開発用：モックデータをリセット
 * 本番環境では何もしない
 */
export const resetMockData = () => {
  if (USE_MOCK_API) {
    mockDistricts = [
      {
        id: 1,
        districtName: '中央地区',
        createdAt: '2024-01-15T09:00:00.000Z',
        updatedAt: '2024-01-15T09:00:00.000Z'
      },
      {
        id: 2,
        districtName: '東部地区',
        createdAt: '2024-01-20T10:30:00.000Z',
        updatedAt: '2024-01-20T10:30:00.000Z'
      },
      {
        id: 3,
        districtName: '西部地区',
        createdAt: '2024-02-01T14:15:00.000Z',
        updatedAt: '2024-02-01T14:15:00.000Z'
      },
      {
        id: 4,
        districtName: '南部地区',
        createdAt: '2024-02-10T16:45:00.000Z',
        updatedAt: '2024-02-10T16:45:00.000Z'
      },
      {
        id: 5,
        districtName: '北部地区',
        createdAt: '2024-03-01T11:20:00.000Z',
        updatedAt: '2024-03-01T11:20:00.000Z'
      }
    ]
    console.log('Collection district mock data has been reset')
  }
}

/**
 * 現在使用中のAPIタイプを取得
 * @returns {string} 'mock' または 'production'
 */
export const getApiType = () => {
  return USE_MOCK_API ? 'mock' : 'production'
}

export default {
  getCollectionDistricts,
  createCollectionDistrict,
  updateCollectionDistrict,
  deleteCollectionDistrict,
  resetMockData,
  getApiType
}
